// Copyright (c) Docugami, Inc. All rights reserved.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssistedAuthoringChunkInfo, Fragment, ChunkInstance, ChunkSuggestion, TokenRange } from '../../networking/api';
import { AppState } from '../../store';
import { useSelector } from 'react-redux';
import { useAaDgWordCanvasProxy } from '../../hooks/aaDgWordCanvasProxy';
import styled from 'styled-components';
import { DgSelectable, DgNonSelectableLabelCss, DgVerticalSpacer, DgFlexSpacer } from '../common/DgCommonStyledComponents';
import { useDocumentDgml } from 'hooks/documentDgml';
import { Logger, LoggerCategory } from 'utils/Logger';
import { Telemetry } from 'telemetry/TelemetryManager';
import { AAFilterAllSchema, AAFilterByInsertSchema, AAFilterByRemoveSchema, AAFilterByReplaceSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { backgroundGray, primaryTheme, primaryThemeSettings, emptyGray } from 'components/shell/presentation/AppStyles';
import { dgFontFamily } from 'components/common/Fonts';
import { useAppSelector } from 'hooks/redux';
import { selectGetAssistedAuthoringChunkInfos, useGetHiddenClauseSuggestionsQuery } from 'store/assistedauthinfo/assistedAuthoringSlice';
import { selectMissingAnomalyInstances, selectSuggestionsInDocOrder } from 'store/assistedauthinfo/selectors';
import { InsertCard, RemoveCard, ReplaceCard } from './ClauseSuggestionsCards';
import { TokenRangeUtils } from 'utils/TokenRangeUtils';
import { scrollIntoViewIfNeeded } from 'components/doc_hosting/taskpane/TaskpaneCardHelpers';
import { useExternalClickHandler, ExternalClickType } from 'hooks/ExternalClickHandler';
import { SlidingPanel } from 'components/cards/SlidingPanel';
import { CardletDropdownLabel } from './ClauseCardletComponents';
import { selectSelectedChunkInstance } from 'store/sdvSelectors';

export interface MissingAnomaly {
    containerChunkSpanId: string;
    followingSiblingSpanId: string;
    precedingSiblingSpanId: string;
    chunkSuggestion: ChunkSuggestion;
}

export const AaScroller = styled.div`
    flex: 1;
    overflow-y: auto;
    background-color: ${backgroundGray};
`;

export const AaCardsList = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px;
    box-sizing: border-box;
`;

const HiddenContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    background-color: ${backgroundGray};
    max-height: 60%;
    border-top: 1px solid ${emptyGray};
`;

const AaClausesSubHeader = styled.div`
    background: linear-gradient(180deg, #3DB535 0%, ${primaryThemeSettings.palette.themePrimary} 100%);
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.25);
    height: 28px;
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const AaClausesFilter = styled.div<DgSelectable>`
    background: ${props => props.selected ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0)'};
    border-radius: 2px;
    margin-top: 2px;
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    height: 24px;
    &:hover {
        background: ${props => props.selected ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.05)'};
    }
`;

const AaClauseFilterText = styled.span`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: 500;
    font-size: 10px;
    line-height: 12px;
    text-align: center;
    ${DgNonSelectableLabelCss}
    color: ${primaryTheme.palette.white};
    padding-left: 10px;
    padding-right: 10px;
`;

const AaShowAllText = styled.span`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: 500;
    font-size: 8px;
    line-height: 10px;
    padding-left: 7px;
    padding-right: 10px;
    ${DgNonSelectableLabelCss}
    color: ${primaryTheme.palette.white};
`;

const ShowContainer = styled.div`
    display: flex;
    justify-content: center;
`;

interface IClauseFilter {
    filter: ClauseType | null;
    label: string;
    sendTelemetryEvent: () => void;
}

export enum ClauseImportance {
    VeryImportant = 1,
    Important = 2,
    NotSoImportant = 4,
    All = VeryImportant | Important | NotSoImportant
};

export enum ClauseType {
    Remove = 'Remove',
    Insert = 'Insert',
    Replace = 'Replace'
}

interface SpanPositionInfo {
    precedingSiblingSpanId: string;
    followingSiblingSpanId: string;
    containerChunkSpanId: string;
}

export interface IClauseSuggestion {
    id: string;
    type: ClauseType;
    importance: ClauseImportance;
    likelihood?: number;
    variants?: Fragment[];
    label?: string;
    containerHeader?: string;
    fragmentLikelihood?: number;
}

export interface IChunkClauseSuggestion extends IClauseSuggestion {
    instance: ChunkInstance;
}

export interface IInsertSuggestion extends IClauseSuggestion {
    type: ClauseType.Insert;
    positionInfo: SpanPositionInfo;
}

export const isInsertSuggestion = (clause: IClauseSuggestion): clause is IInsertSuggestion => clause.type === ClauseType.Insert;

export interface ClauseSuggestionsListProps {
    viewAllVariations: (label: string) => void;
    spanIdsInContext: string[];
    documentId: string;
}

export const ClauseSuggestionsList = (props: ClauseSuggestionsListProps): JSX.Element => {
    const [activeFilter, setActiveFilter] = useState<ClauseType | null>(null);
    const [selectedClauseId, setSelectedClauseId] = useState('');

    const curFragment = useRef<Fragment>();
    const [showHiddenClauses, setShowHiddenClauses] = useState(false);

    const [clauseCardClicked, setClauseCardClicked] = useState(false);

    const { data: documentHiddenClauses } = useGetHiddenClauseSuggestionsQuery(props.documentId);
    const hiddenClauseIds = useMemo(() => !!documentHiddenClauses ? [...documentHiddenClauses.existingSpanIds, ...documentHiddenClauses.clauseSuggestionIds] : [], [documentHiddenClauses]);

    const dgml = useDocumentDgml();
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);

    const missingInstancesInDoc = useAppSelector((state) => selectMissingAnomalyInstances(state, props.documentId));
    const assistedAuthoringIds = useAppSelector((state) => selectGetAssistedAuthoringChunkInfos(state, props.documentId)?.ids as string[]);

    const suggestionsInDocOrder = useAppSelector((state) => selectSuggestionsInDocOrder(state, props.documentId));
    const knownChunks = useAppSelector((state) => state.assistedAuthoringInfo.knownChunks);
    const contextualMode = useSelector((state: AppState) => state.settings.assistedAuthoring.contextMode);

    const dgWordCanvasProxy = useAaDgWordCanvasProxy(props.documentId);

    const scrollRef = useRef<HTMLDivElement>(null);
    const handleClickOutside = useCallback(() => { setSelectedClauseId(''); setClauseCardClicked(false); curFragment.current = undefined; }, []);
    const isSelectedClauseHidden = useMemo(() => !!hiddenClauseIds.includes(selectedClauseId), [hiddenClauseIds, selectedClauseId]);
    const listRef = useExternalClickHandler<HTMLDivElement>(ExternalClickType.Children, handleClickOutside, !selectedClauseId || isSelectedClauseHidden);
    const hiddenListRef = useExternalClickHandler<HTMLDivElement>(ExternalClickType.Children, handleClickOutside, !selectedClauseId || !isSelectedClauseHidden);

    const instancesWithInfo: Record<string, ChunkInstance> = useMemo(() => {
        const res: Record<string, ChunkInstance> = {};

        if (!dgml.isInitialized() || !assistedAuthoringIds) {
            return res;
        }

        for (const instanceId of assistedAuthoringIds) {
            const instance = dgml.getInstanceById(instanceId);

            if (!instance) {
                Logger.LogError(LoggerCategory.AssistedAuthoring, `no assistedauthoring info found for ${instanceId}`);
                continue;
            }

            res[instance.spanId!] = instance;
        }

        return res;
    }, [dgml, assistedAuthoringIds])

    // When selection changes on the document, scroll the list to the relevant selected clause suggestion
    useEffect(() => {
        if (!selectedChunkInstance?.spanId) {
            return;
        }

        if (clauseCardClicked) {
            // This selection is coming from a user clause cardlet selection
            setClauseCardClicked(false);
            return;
        }

        const selectionTokenRange = selectedChunkInstance.tokenRange;
        if (!selectionTokenRange) {
            return;
        }

        // If we currently have a selected clause and the document selection is within that clause,
        // we should preserve the selected clause card
        const currentSelectedClause = displayClauses.find(x => x.id === selectedClauseId);
        if (!!currentSelectedClause) {
            if (isInsertSuggestion(currentSelectedClause)) {
                const prevTokenRange = dgml.getInstanceById(currentSelectedClause.positionInfo.precedingSiblingSpanId)?.tokenRange;
                if (!!prevTokenRange && TokenRangeUtils.rangeIsContained(selectionTokenRange!, prevTokenRange)) {
                    return;
                }
                const nextTokenRange = dgml.getInstanceById(currentSelectedClause.positionInfo.followingSiblingSpanId)?.tokenRange;
                if (!!nextTokenRange && TokenRangeUtils.rangeIsContained(selectionTokenRange!, nextTokenRange)) {
                    return;
                }
            } else {
                const tokenRange = instancesWithInfo[currentSelectedClause.id]?.tokenRange;
                if (!!tokenRange && TokenRangeUtils.rangeIsContained(selectionTokenRange!, tokenRange)) {
                    return;
                }
            }

            if (isSelectedClauseHidden) {
                return;
            }
        }

        let clauseToSelect: IClauseSuggestion | null = null;
        for (const clause of displayClauses) {
            let currTokenRange: TokenRange | undefined;

            // Get the current clause's token range
            if (!isInsertSuggestion(clause)) {
                currTokenRange = instancesWithInfo[clause.id]?.tokenRange;
            } else {
                // Prefer the preceding sibling span id if it exists
                const siblingSpanId = clause.positionInfo.precedingSiblingSpanId || clause.positionInfo.followingSiblingSpanId;
                currTokenRange = dgml.getInstanceById(siblingSpanId)?.tokenRange;
            }

            if (!currTokenRange) {
                continue;
            }

            // Check if the current clause contains the selected chunk
            const rangeWithin = TokenRangeUtils.rangeIsContained(selectionTokenRange, currTokenRange);
            if (rangeWithin && (!clauseToSelect || clause.importance > clauseToSelect.importance)) {
                clauseToSelect = clause;
                if (clause.importance === ClauseImportance.VeryImportant) {
                    // We've found an important matching clause, no need to keep iterating, we can just select this one
                    break;
                }
            }

            // Displayed clauses are sorted in doc order, so if the current clause has passed the selected chunk range,
            // we can assume we will not find another matching clause
            if (selectionTokenRange.toTok < currTokenRange.toTok) {
                if (!clauseToSelect) {
                    // We have not found a matching clause, so the current clause is the closest border chunk to the selected chunk
                    // Scroll the current clause into view so the clause list mirrors the document position
                    scrollClauseIntoView(clause.id);
                    return;
                }
                break;
            }
        }

        if (!!clauseToSelect) {
            setSelectedClauseId(clauseToSelect.id);
        }
    },  // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedChunkInstance])

    function scrollClauseIntoView(clauseId: string) {
        if (!scrollRef.current || isSelectedClauseHidden) {
            return;
        }

        const child = scrollRef.current.querySelector(`[id='${clauseId}']`);
        if (!!child) {
            scrollIntoViewIfNeeded(child, scrollRef.current, { block: "nearest" });
        }
    }

    useEffect(() => {
        if (!!selectedClauseId) {
            scrollClauseIntoView(selectedClauseId);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedClauseId])

    function selectClause(id: string, fragment?: Fragment): void {
        setClauseCardClicked(true);
        setSelectedClauseId(id);
        curFragment.current = fragment;

        if (id === selectedClauseId) {
            return;
        }

        const instance = instancesWithInfo[id];
        if (instance) {
            dgWordCanvasProxy?.selectParagraphContainingChunk(instance.spanId ?? "");
        } else {
            let missing = missingInstancesInDoc.find(x => x.chunkSuggestion.id === id);
            if (missing) {
                dgWordCanvasProxy?.setSelectionIP(missing.precedingSiblingSpanId, missing.followingSiblingSpanId, missing.containerChunkSpanId!);
            }
        }
    }

    function getExistingClauseType(chunkInfo: AssistedAuthoringChunkInfo): { type: ClauseType, importance: ClauseImportance } | null {
        if (chunkInfo.likelihood < .2) {
            return ({
                type: ClauseType.Remove,
                importance: chunkInfo.likelihood === 0 ? ClauseImportance.VeryImportant : ClauseImportance.Important
            });
        } else if (!!chunkInfo.variants?.length) {
            return ({
                type: ClauseType.Replace,
                importance: chunkInfo.variants.findIndex(x => x.likelihood > chunkInfo.fragmentLikelihood) !== -1 ? ClauseImportance.VeryImportant : ClauseImportance.NotSoImportant
            });
        }
        return null;
    }

    function getInsertClauseImportance(likelihood: number | undefined): ClauseImportance | undefined {
        if (likelihood !== undefined && likelihood !== 0) {
            return likelihood >= 0.75 ? ClauseImportance.VeryImportant : likelihood < .5 && likelihood >= .1 ? ClauseImportance.NotSoImportant : ClauseImportance.Important;
        }
    }

    const clauses = useMemo(() => {
        const allClauses: IClauseSuggestion[] = [];
        for (const suggestion of suggestionsInDocOrder) {
            const { type, importance } = getExistingClauseType(suggestion) ?? { type: null, importance: null };
            if (type !== null) {
                const instance = instancesWithInfo[suggestion.spanId];
                if (!!instance && !!knownChunks.find(x => x.Id === instance.spanId) &&
                    (type !== ClauseType.Replace || !!suggestion.variants.length)) {
                    const chunkClause: IChunkClauseSuggestion = {
                        type: type,
                        id: suggestion.spanId,
                        instance: instance,
                        importance: importance!,
                        likelihood: suggestion.likelihood,
                        variants: suggestion.variants,
                        label: suggestion.suggestedLabel,
                        fragmentLikelihood: suggestion.fragmentLikelihood,
                        containerHeader: suggestion.containerHeader
                    }

                    allClauses.push(chunkClause);
                }
            }

            for (const chunkSuggestion of suggestion.suggestedChunks) {
                const insertClauseImportance = getInsertClauseImportance(chunkSuggestion.likelihood);
                if (!!insertClauseImportance && !!chunkSuggestion.variants?.length &&
                    (!!knownChunks.find(x => x.Id === chunkSuggestion.id) || !!knownChunks.find(x => x.Id === suggestion.spanId))) {
                    const insertClause: IInsertSuggestion = {
                        type: ClauseType.Insert,
                        id: chunkSuggestion.id ?? '',
                        importance: insertClauseImportance,
                        likelihood: chunkSuggestion.likelihood,
                        variants: chunkSuggestion.variants,
                        label: chunkSuggestion.label,
                        positionInfo: {
                            precedingSiblingSpanId: chunkSuggestion.precedingSiblingId ?? '',
                            followingSiblingSpanId: chunkSuggestion.followingSiblingId ?? '',
                            containerChunkSpanId: suggestion.spanId!
                        }
                    };

                    allClauses.push(insertClause);
                }
            }
        }

        return allClauses;
    }, [suggestionsInDocOrder, instancesWithInfo, knownChunks])

    // Clauses to be displayed are filtered on the active filter set, or contextual span position if contextual mode is set
    const { displayClauses, hiddenClauses } = useMemo(() => {
        const displayClauses: IClauseSuggestion[] = [];
        const hiddenClauses: IClauseSuggestion[] = [];

        function addClause(clause: IClauseSuggestion) {
            if (hiddenClauseIds.includes(clause.id)) {
                hiddenClauses.push(clause);
            } else {
                displayClauses.push(clause);
            }
        }

        for (const clause of clauses) {
            if (activeFilter !== null && clause.type !== activeFilter) {
                // Skip clauses that are not of the actively filtered clause type
                continue;
            }

            if (!!contextualMode) {
                // Skip clauses that are not within the document's current display context
                if (isInsertSuggestion(clause)) {
                    if (!props.spanIdsInContext.includes(clause.positionInfo.followingSiblingSpanId) &&
                        !props.spanIdsInContext.includes(clause.positionInfo.precedingSiblingSpanId) &&
                        !props.spanIdsInContext.includes(clause.positionInfo.containerChunkSpanId)) {
                        continue;
                    }
                } else if (!props.spanIdsInContext.includes(clause.id)) {
                    continue;
                }
            }

            addClause(clause);
        }

        return {
            displayClauses: displayClauses,
            hiddenClauses: hiddenClauses
        };
    }, [clauses, activeFilter, contextualMode, props.spanIdsInContext, hiddenClauseIds])

    function renderItem(clause: IClauseSuggestion, hidden: boolean) {
        switch (clause.type) {
            case ClauseType.Insert:
                return (
                    <InsertCard
                        key={clause.id}
                        item={clause as IInsertSuggestion}
                        documentId={props.documentId}
                        selectedCardId={selectedClauseId}
                        selectClause={selectClause}
                        currentFragment={curFragment.current as Fragment}
                        dgWordCanvasProxy={dgWordCanvasProxy}
                        hidden={hidden} />
                )

            case ClauseType.Remove:
                return (
                    <RemoveCard
                        key={clause.id}
                        item={clause as IChunkClauseSuggestion}
                        documentId={props.documentId}
                        selectedCardId={selectedClauseId}
                        selectClause={selectClause}
                        dgml={dgml}
                        dgWordCanvasProxy={dgWordCanvasProxy}
                        hidden={hidden} />
                )

            case ClauseType.Replace:
                return (
                    <ReplaceCard
                        key={clause.id}
                        item={clause as IChunkClauseSuggestion}
                        documentId={props.documentId}
                        selectedCardId={selectedClauseId}
                        selectClause={selectClause}
                        currentFragment={curFragment.current as Fragment}
                        dgWordCanvasProxy={dgWordCanvasProxy}
                        hidden={hidden} />
                )

            default:
                return null;
        }
    }

    return (
        <div key="tp" className="taskpanePage">
            <ClauseTypeFiltersPane activeFilter={activeFilter} onFilterChanged={setActiveFilter} />
            <AaScroller ref={scrollRef} key="1">
                <AaCardsList key="cl" ref={listRef}>
                    {displayClauses.map(item => renderItem(item, false))}
                </AaCardsList>
            </AaScroller>
            <HiddenContainer>
                <CardletDropdownLabel
                    fontSize={10}
                    label={showHiddenClauses ? "COLLAPSE HIDDEN" : "SHOW HIDDEN"}
                    dropdownOpen={showHiddenClauses}
                    disabled={!hiddenClauses.length}
                    onClick={() => setShowHiddenClauses(v => !v)} />
                <SlidingPanel isOpen={showHiddenClauses} scrollable>
                    <AaScroller key="1">
                        <AaCardsList key="cl" ref={hiddenListRef}>
                            {hiddenClauses.map(item => renderItem(item, true))}
                        </AaCardsList>
                    </AaScroller>
                </SlidingPanel>
            </HiddenContainer>
        </div>
    )
}


interface ClauseTypeFiltersProps {
    activeFilter: ClauseType | null;
    onFilterChanged: (filter: ClauseType | null) => void;
}

const ClauseTypeFiltersPane = (props: ClauseTypeFiltersProps): JSX.Element => {
    const filters: IClauseFilter[] = [
        {
            filter: null,
            label: "All",
            //telemetryEvent: TelemTelemetryEvent.AAFilterAll
            sendTelemetryEvent: () => Telemetry.trackEvent<AAFilterAllSchema>({
                eventName: TelemetryEvent.AAFilterAll
            })
        },
        {
            filter: ClauseType.Insert,
            label: "Insert",
            //telemetryEvent: TelemetryEvent.AAFilterByInsert
            sendTelemetryEvent: () => Telemetry.trackEvent<AAFilterByInsertSchema>({
                eventName: TelemetryEvent.AAFilterByInsert
            })
        },
        {
            filter: ClauseType.Remove,
            label: "Remove",
            //telemetryEvent: TelemetryEvent.AAFilterByRemove
            sendTelemetryEvent: () => Telemetry.trackEvent<AAFilterByRemoveSchema>({
                eventName: TelemetryEvent.AAFilterByRemove
            })
        },
        {
            filter: ClauseType.Replace,
            label: "Replace",
            //telemetryEvent: TelemetryEvent.AAFilterByReplace
            sendTelemetryEvent: () => Telemetry.trackEvent<AAFilterByReplaceSchema>({
                eventName: TelemetryEvent.AAFilterByReplace
            })
        }
    ]

    return (
        <DgVerticalSpacer key="vs_filters" height={"28px"}>
            <AaClausesSubHeader key="sub_filters">
                <ShowContainer key="sat">
                    <AaShowAllText>SHOW:</AaShowAllText>
                </ShowContainer>
                {
                    filters.map(filter => (
                        <React.Fragment key={`ff_${filter.filter}`}>
                            <AaClausesFilter
                                key={`cf_${filter.filter}`}
                                selected={props.activeFilter === filter.filter}
                                onClick={() => {
                                    props.onFilterChanged(filter.filter);
                                    filter.sendTelemetryEvent();
                                }}>
                                <AaClauseFilterText>{filter.label.toUpperCase()}</AaClauseFilterText>
                            </AaClausesFilter>
                            <DgFlexSpacer key={`fs_${filter.filter}`} />
                        </React.Fragment>
                    ))
                }
            </AaClausesSubHeader>
        </DgVerticalSpacer>
    )
}
